import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../index";

/**
 * Onboarding State Interface
 *
 * Tracks which onboarding experiences the user has completed.
 * This state is persisted to localStorage via redux-persist.
 */
export interface OnboardingState {
  /** Whether the user has seen the entrance animation */
  hasSeenEntranceAnimation: boolean;
  /** Whether the user has seen the first login welcome page */
  hasSeenFirstLoginWelcome: boolean;
  /** Whether the user has given GDPR consent (checked consent banner) */
  hasGivenGdprConsent: boolean;
}

/**
 * Initial State
 */
const initialState: OnboardingState = {
  hasSeenEntranceAnimation: false,
  hasSeenFirstLoginWelcome: false,
  hasGivenGdprConsent: false,
};

/**
 * Onboarding Slice
 *
 * Manages onboarding state for animation and welcome page tracking.
 * Used to ensure users only see onboarding experiences once.
 */
const onboardingSlice = createSlice({
  name: "onboarding",
  initialState,
  reducers: {
    /**
     * Marks the entrance animation as seen
     *
     * Called after the entrance animation completes.
     * Prevents the animation from showing on subsequent visits.
     */
    setEntranceAnimationSeen: (state) => {
      state.hasSeenEntranceAnimation = true;
    },

    /**
     * Marks the first login welcome page as seen
     *
     * Called after the user dismisses or completes the welcome page.
     * Prevents the welcome page from showing on subsequent logins.
     */
    setFirstLoginWelcomeSeen: (state) => {
      state.hasSeenFirstLoginWelcome = true;
    },

    /**
     * Sets the GDPR consent state
     *
     * Called when user checks/unchecks consent banner during onboarding.
     * Persisted to maintain consent state across page navigations.
     */
    setGdprConsent: (state, action: PayloadAction<boolean>) => {
      state.hasGivenGdprConsent = action.payload;
    },

    /**
     * Resets all onboarding flags to initial state
     *
     * Used for testing or when a user wants to re-experience onboarding.
     */
    resetOnboarding: () => initialState,
  },
});

/**
 * Actions
 */
export const {
  setEntranceAnimationSeen,
  setFirstLoginWelcomeSeen,
  setGdprConsent,
  resetOnboarding,
} = onboardingSlice.actions;

/**
 * Selectors
 */
export const selectHasSeenEntranceAnimation = (state: RootState) =>
  state.onboarding.hasSeenEntranceAnimation;

export const selectHasSeenFirstLoginWelcome = (state: RootState) =>
  state.onboarding.hasSeenFirstLoginWelcome;

export const selectHasGivenGdprConsent = (state: RootState) =>
  state.onboarding.hasGivenGdprConsent;

/**
 * Reducer
 */
export default onboardingSlice.reducer;
